import { ref } from 'vue'
import axios from 'axios'
import store from '@/store'
import api from '@/store/api/donor'
import { mapDonationCharityBranding } from '@/helpers/charityAvatar'
import { estimatedGiftAidPence } from '@/helpers/giftAidDeclared'
import type { DonorDonation } from '@/types/donorFinancialYear'
import type {
  ManualGivingDonationInput,
  ManualGivingDonationUpdate,
} from '@/types/givingRecord'

/**
 * The manual-donations endpoints (GET/POST/PUT/DELETE /me/donations/manual) are
 * paid-only on the real API; free plans get 403 and a global error toast.
 * Mirage always serves manual donations regardless of plan (no 403).
 */
function canFetchManualDonations(): boolean {
  if (import.meta.env.MODE === 'mockapi') {
    return true
  }
  return store.getters.hasFullGivingRecord === true
}

const manualDonations = ref<DonorDonation[]>([])
const isReady = ref(false)
const isLoading = ref(false)

let loadPromise: Promise<void> | null = null
let givingRecordWatchStarted = false

/** Reload manual donations when plan features grant fullGivingRecord (real API). */
function startGivingRecordWatch() {
  if (givingRecordWatchStarted) return
  givingRecordWatchStarted = true

  store.watch(
    (_state, getters) => getters.hasFullGivingRecord === true,
    (enabled) => {
      if (import.meta.env.MODE === 'mockapi') {
        if (!isReady.value && !isLoading.value) {
          void loadManualDonations()
        }
        return
      }
      if (enabled) {
        if (!isReady.value && !isLoading.value) {
          void loadManualDonations()
        }
        return
      }
      manualDonations.value = []
      isReady.value = false
      loadPromise = null
    },
    { immediate: true }
  )
}

function unwrapDonationPayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {}
  const obj = data as Record<string, unknown>
  if (obj.donation && typeof obj.donation === 'object') {
    return obj.donation as Record<string, unknown>
  }
  return obj
}

function mapApiDonation(row: Record<string, unknown>): DonorDonation {
  const receipts = Array.isArray(row.receipts)
    ? row.receipts.map((r) => {
        const receipt = r as Record<string, unknown>
        return {
          id: String(receipt.id ?? ''),
          filename: String(receipt.filename ?? 'Receipt'),
          url: String(receipt.url ?? '#'),
          uploadedAt:
            typeof receipt.uploadedAt === 'string' ? receipt.uploadedAt : undefined,
        }
      })
    : undefined

  return {
    id: String(row.id ?? ''),
    state: String(row.state ?? 'declared'),
    charity: String(row.charity ?? ''),
    amount: Number(row.amount ?? 0),
    giftaid: Number(row.giftaid ?? 0),
    giftAidDeclared:
      typeof row.giftAidDeclared === 'boolean'
        ? row.giftAidDeclared
        : Number(row.giftaid ?? 0) > 0,
    time: String(row.time ?? ''),
    source: 'manual',
    sourceCategory: 'manual',
    notes: typeof row.notes === 'string' ? row.notes : undefined,
    receipts,
    giftAidHuntAccepted: row.giftAidHuntAccepted === true,
    matchMethod: typeof row.matchMethod === 'string' ? row.matchMethod : undefined,
    organisationType:
      row.organisationType === 'Charity' || row.organisationType === 'CASC'
        ? row.organisationType
        : undefined,
    retrospective: row.retrospective === true,
    issuedStatementPeriod:
      typeof row.issuedStatementPeriod === 'string' ? row.issuedStatementPeriod : undefined,
    recurringSeriesId:
      typeof row.recurringSeriesId === 'string' ? row.recurringSeriesId : undefined,
    scheduledOccurrenceDate:
      typeof row.scheduledOccurrenceDate === 'string'
        ? row.scheduledOccurrenceDate
        : undefined,
    individuallyEdited: row.individuallyEdited === true,
    ...mapDonationCharityBranding(row),
  }
}

async function loadManualDonations(force = false): Promise<void> {
  // Skip entirely on free plans, calling the endpoint 403s and pops an error.
  // Leave isReady false so the fetch runs if the donor upgrades later.
  if (!canFetchManualDonations()) {
    manualDonations.value = []
    return
  }
  if (!force && isReady.value) return
  if (!force && loadPromise) return loadPromise

  const run = async () => {
    isLoading.value = true
    try {
      const response = await api.fetchManualDonations()
      const rows = (response.data?.donations ?? []) as Record<string, unknown>[]
      manualDonations.value = rows.map(mapApiDonation)
      isReady.value = true
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        manualDonations.value = []
        isReady.value = true
        return
      }
      console.error('Failed to load manual donations', error)
      // Do not leave paid views (claim guide) spinning if this GET fails.
      isReady.value = true
      throw error
    } finally {
      isLoading.value = false
      loadPromise = null
    }
  }

  loadPromise = run()
  return loadPromise
}

/** Force reload donor-added donations (e.g. after accepting a found-for-you item). */
export async function refreshManualDonations(): Promise<void> {
  return loadManualDonations(true)
}

/**
 * Donor-added donations from GET/POST /me/donations/manual (Mirage in dev:mock-api).
 */
export function useManualGivingDonations() {
  startGivingRecordWatch()

  if (!isReady.value && !isLoading.value && !loadPromise) {
    void loadManualDonations()
  }

  const addManualDonation = async (input: ManualGivingDonationInput): Promise<DonorDonation> => {
    const giftAidDeclared = input.giftAidDeclared
    const giftaid = giftAidDeclared ? estimatedGiftAidPence(input.amountPence) : 0
    const payload = {
      charity: input.charity.trim(),
      amount: input.amountPence,
      giftAidDeclared,
      giftaid,
      time: input.date,
      notes: input.notes?.trim(),
    }
    const response = await api.postManualDonation(payload)
    // Paint from the form values first so a sparse live POST body still
    // appears in the list without a refresh. Overlay any id the API returns.
    const raw = unwrapDonationPayload(response.data)
    const entry = mapApiDonation({
      ...payload,
      source: 'manual',
      sourceCategory: 'manual',
      ...raw,
      id: String(raw.id ?? `manual-${Date.now()}`),
    })
    manualDonations.value = [
      entry,
      ...manualDonations.value.filter((row) => row.id !== entry.id),
    ]
    isReady.value = true
    return entry
  }

  const updateManualDonation = async (
    id: string,
    input: ManualGivingDonationUpdate
  ): Promise<DonorDonation> => {
    const existing = manualDonations.value.find((row) => row.id === id)
    const giftAidDeclared =
      typeof input.giftAidDeclared === 'boolean'
        ? input.giftAidDeclared
        : existing?.giftAidDeclared === true
    const amountPence = input.amountPence ?? existing?.amount ?? 0
    const payload: {
      charity?: string
      amount?: number
      giftAidDeclared?: boolean
      giftaid?: number
      time?: string
      notes?: string
    } = {
      charity: input.charity?.trim(),
      amount: input.amountPence,
      giftAidDeclared: input.giftAidDeclared,
      giftaid:
        typeof input.giftAidDeclared === 'boolean' || typeof input.amountPence === 'number'
          ? giftAidDeclared
            ? estimatedGiftAidPence(amountPence)
            : 0
          : undefined,
      notes: input.notes !== undefined ? input.notes.trim() : undefined,
    }
    // Omit time when the donor did not change the date, including a notes-only
    // save. An older gift can then be corrected without resubmitting its day.
    if (input.date) payload.time = input.date
    const response = await api.putManualDonation(id, payload)
    const entry = mapApiDonation(response.data as Record<string, unknown>)
    manualDonations.value = manualDonations.value.map((row) =>
      row.id === id ? entry : row
    )
    isReady.value = true
    return entry
  }

  const updateManualDonationNotes = async (
    id: string,
    notes?: string
  ): Promise<DonorDonation> => {
    return updateManualDonation(id, { notes })
  }

  const deleteManualDonation = async (id: string): Promise<void> => {
    await api.deleteManualDonation(id)
    manualDonations.value = manualDonations.value.filter((row) => row.id !== id)
  }

  return {
    manualDonations,
    isReady,
    isLoading,
    loadManualDonations,
    addManualDonation,
    updateManualDonation,
    updateManualDonationNotes,
    deleteManualDonation,
  }
}
